import { Empty } from '@bufbuild/protobuf';

import api from '@/shared/api/api';
import {
  AuthState,
  MFA_DisableReq,
  MFA_Enroll_BeginReq,
  MFA_Enroll_BeginResp,
  MFA_Enroll_ConfirmReq,
  MFA_Enroll_ConfirmResp,
  MFA_SignIn_VerifyReq,
  MFAStatus,
  Presign,
} from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { Authentication } from '@/shared/api/protocol-ts/api/auth/svc_authentication_connectweb';
import {
  CheckInviteTokenReq,
  CheckInviteTokenResp,
  LogoutReq,
  OrganizationAPIKey_CreateReq,
  OrganizationAPIKey_CreateResp,
  OrganizationAPIKey_DeleteReq,
  OrganizationAPIKey_DeleteResp,
  OrganizationAPIKey_ListReq,
  OrganizationAPIKey_ListResp,
  PasswordPolicy_GetReq,
  PasswordPolicy_GetResp,
  Recover_ByEmail_1_StartReq,
  Recover_ByEmail_2_SetPasswordReq,
  SignInReq,
  SignUp_ByOneself_1_SendUserInfoReq,
  SignUp_ByOneself_2_ConfirmIdentityReq,
  SignUp_ByOneself_3_SetPasswordReq,
  SignUp_ViaOrganizationInviteReq,
  SignUp_ViaSalesmanInviteReq,
  SignUp_ViaSharedPatientReq,
  SignUp_ViaSharedPatientResp,
} from '@/shared/api/protocol-ts/api/auth/svc_authentication_pb';
import { SliceName } from '@/shared/config';
import { createThunkGenerator } from '@/shared/lib';

export type AuthRequests = keyof typeof Authentication.methods;

const generateAuthThunk = createThunkGenerator<AuthRequests>(SliceName.auth);

export const startSignIn = generateAuthThunk<SignInReq, AuthState>(
  'signIn',
  api.auth.signIn,
);

export const signInByPresign = generateAuthThunk<Presign, AuthState>(
  'signInByPresign',
  api.auth.signInByPresign,
);

export const logout = generateAuthThunk<LogoutReq, Empty>(
  'logout',
  api.auth.logout,
);

export const signUp = generateAuthThunk<
  SignUp_ByOneself_1_SendUserInfoReq,
  AuthState
>('signUp_ByOneself_1_SendUserInfo', api.auth.signUp_ByOneself_1_SendUserInfo);

export const signUpViaSharedPatient = generateAuthThunk<
  SignUp_ViaSharedPatientReq,
  SignUp_ViaSharedPatientResp
>('signUp_ViaSharedPatient', api.auth.signUp_ViaSharedPatient);

export const signUpViaOrganizationInvite = generateAuthThunk<
  SignUp_ViaOrganizationInviteReq,
  AuthState
>('signUp_ViaOrganizationInvite', api.auth.signUp_ViaOrganizationInvite);

export const signUpViaSalesmanInvite = generateAuthThunk<
  SignUp_ViaSalesmanInviteReq,
  AuthState
>('signUp_ViaSalesmanInvite', api.auth.signUp_ViaSalesmanInvite);

export const confirmEmail = generateAuthThunk<
  SignUp_ByOneself_2_ConfirmIdentityReq,
  AuthState
>(
  'signUp_ByOneself_2_ConfirmIdentity',
  api.auth.signUp_ByOneself_2_ConfirmIdentity,
);

export const confirmPassword = generateAuthThunk<
  SignUp_ByOneself_3_SetPasswordReq,
  AuthState
>('signUp_ByOneself_3_SetPassword', api.auth.signUp_ByOneself_3_SetPassword);

export const checkInviteToken = generateAuthThunk<
  CheckInviteTokenReq,
  CheckInviteTokenResp
>('checkInviteToken', api.auth.checkInviteToken);

export const recoverByEmail = generateAuthThunk<
  Recover_ByEmail_1_StartReq,
  AuthState
>('recover_ByEmail_1_Start', api.auth.recover_ByEmail_1_Start);

export const setPassword = generateAuthThunk<
  Recover_ByEmail_2_SetPasswordReq,
  AuthState
>('recover_ByEmail_2_SetPassword', api.auth.recover_ByEmail_2_SetPassword);

export const getPasswordPolicy = generateAuthThunk<
  PasswordPolicy_GetReq,
  PasswordPolicy_GetResp
>('passwordPolicy_Get', api.auth.passwordPolicy_Get);

export const listOrganizationApiKeys = generateAuthThunk<
  OrganizationAPIKey_ListReq,
  OrganizationAPIKey_ListResp
>('organizationAPIKey_List', api.auth.organizationAPIKey_List);

export const createOrganizationApiKey = generateAuthThunk<
  OrganizationAPIKey_CreateReq,
  OrganizationAPIKey_CreateResp
>('organizationAPIKey_Create', api.auth.organizationAPIKey_Create);

export const deleteOrganizationApiKey = generateAuthThunk<
  OrganizationAPIKey_DeleteReq,
  OrganizationAPIKey_DeleteResp
>('organizationAPIKey_Delete', api.auth.organizationAPIKey_Delete);

export const mfaSignInVerify = generateAuthThunk<
  MFA_SignIn_VerifyReq,
  AuthState
>('mFA_SignIn_Verify', api.auth.mFA_SignIn_Verify);

export const mfaEnrollBegin = generateAuthThunk<
  MFA_Enroll_BeginReq,
  MFA_Enroll_BeginResp
>('mFA_Enroll_Begin', api.auth.mFA_Enroll_Begin);

export const mfaEnrollConfirm = generateAuthThunk<
  MFA_Enroll_ConfirmReq,
  MFA_Enroll_ConfirmResp
>('mFA_Enroll_Confirm', api.auth.mFA_Enroll_Confirm);

export const mfaDisable = generateAuthThunk<MFA_DisableReq, Empty>(
  'mFA_Disable',
  api.auth.mFA_Disable,
);

export const mfaStatus = generateAuthThunk<Empty, MFAStatus>(
  'mFA_Status',
  api.auth.mFA_Status,
);
